import { type ChatMessage, type ChatMessageWithMetadata } from "@fitness-app/data-models/entities/Chat";

import { mergeLatestMessages } from "./mergeLatestMessages";

type StoredMessage = ChatMessage | ChatMessageWithMetadata;

export type MessageSendStatus = ChatMessageWithMetadata["status"];

/** Only the local copy of a message the sender added optimistically carries a status — a row from the database never does. */
export const getMessageSendStatus = (message: StoredMessage): MessageSendStatus | undefined =>
  "status" in message ? message.status : undefined;

export const isUnsentMessage = (message: StoredMessage): boolean => {
  const status = getMessageSendStatus(message);

  return status === "sending" || status === "failed";
};

/**
 * A first page load used to replace the list wholesale and with it the sender's optimistic copy
 * of a message that had not been saved yet, so a message still on its way (or one that failed)
 * vanished on reopening the chat as if it had been delivered (TRE-2263). Local unsent copies stay;
 * a row the database already holds wins over its local copy, which is how a lost response
 * resolves itself to "sent".
 */
export const keepUnsentMessages = (existing: StoredMessage[], latest: StoredMessage[]): StoredMessage[] => {
  const unsent = existing.filter(isUnsentMessage);

  return unsent.length ? mergeLatestMessages(unsent, latest) : latest;
};

/**
 * A message is "sending" only while its request is actually running. Anything else still marked
 * so (the request died with the app in the background, a paused retry) is shown as failed, so the
 * sender can resend it instead of trusting a bubble that looks delivered.
 */
export const failStuckMessages = (messages: StoredMessage[], inFlightIds: ReadonlySet<string>): StoredMessage[] => {
  let changed = false;
  const next = messages.map((message) => {
    if (getMessageSendStatus(message) === "sending" && !inFlightIds.has(message.id)) {
      changed = true;
      return { ...message, status: "failed" as const };
    }
    return message;
  });

  return changed ? next : messages;
};

/** A copy realtime or a refetch already replaced with the saved row has no status any more and stays delivered. */
export const markMessageFailed = (messages: StoredMessage[], messageId: string): StoredMessage[] =>
  messages.map((message) =>
    message.id === messageId && isUnsentMessage(message) ? { ...message, status: "failed" as const } : message,
  );
